import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  ButtonBase,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Card from '@mui/material/Card';
import { loadStripe } from '@stripe/stripe-js';

import { checkoutAPIService } from '~/api/checkout';
import PhonesImage from '~/assets/images/payments/phones.png';
import Notification from '~/components/organisms/Notification/Notification';
import { AlertContext } from '~/components/organisms/Notification/utils/AlertContextProvider';
import { Button } from '~/components/ui/button';
import { Text } from '~/components/ui/text';
import { googleAnalytics } from '~/services/google-analytics-service';
import { getLoggedInUser } from '~/store/reducers/account/selector';
import { COLORS } from '~/styles/colors';
import { getBillingTranslation } from '~/utils/common';
import { logger } from '~/utils/logger';

import * as S from './styles';

export default function Payments() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [prices, setPrices] = useState([]);
  const [loader, setLoader] = useState(false);
  const [currencyLoader, setCurrencyLoader] = useState(false);
  const [openBenefits, setOpenBenefits] = useState(false);
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('currency') || 'usd';
  });
  const [publicKey, setPublicKey] = useState(null);
  const [cabinetLink, setCabinetLink] = useState(null);
  const [activeSubscription, setActiveSubscription] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [isBuyOrManageSubscriptionLoading, setIsBuyOrManageSubscriptionLoading] = useState(false);

  const { actions } = useContext(AlertContext);
  const { isSubscriptionValid, isTrialSubscription, subscriptionInfo } = useSelector(getLoggedInUser);

  const handleChangeCurrency = async (event) => {
    setCurrencyLoader(true);
    const value = event?.target?.value?.toLowerCase();
    setCurrency(value);
    localStorage.setItem('currency', value);
    const checkoutPrices = await checkoutAPIService.getPrices(value);
    setPrices(checkoutPrices?.data?.[0]?.prices || []);
    setCurrencyLoader(false);
  };

  const handleChangeSubPeriod = (intervalCount) => {
    setSelectedPeriod(intervalCount);
  };

  useEffect(() => {
    if (activeSubscription) {
      setSelectedPeriod(activeSubscription.intervalCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubscription.intervalCount]);

  const handleBuyPress = async () => {
    setIsBuyOrManageSubscriptionLoading(true);

    if (cabinetLink) {
      window.location.href = cabinetLink;

      return;
    }

    const stripe = await loadStripe(publicKey);
    const currentPrice = prices.find((plan) => plan?.recurring?.intervalCount === Number(selectedPeriod));

    if (currentPrice) {
      const response = await checkoutAPIService.createPriceSession(currentPrice.id, currency);
      const sessionId = response?.data?.sessionId;

      const eventParams = {
        transactionId: sessionId,
        fullPrice: currentPrice.specialPrice || currentPrice.unitAmount,
        currency,
        itemId: currentPrice.id,
        subscriptionPeriod: `${currentPrice.recurring?.intervalCount} ${currentPrice.recurring?.interval}`,
      };

      await googleAnalytics.events.startCheckout(eventParams);

      const { error } = await stripe.redirectToCheckout({ sessionId });
      setIsBuyOrManageSubscriptionLoading(false);
      if (error) {
        logger.error('Error redirecting to Stripe Checkout:', error);
      }
    }
  };

  const loadCheckoutData = async () => {
    setLoader(true);

    const checkoutPrices = await checkoutAPIService.getPrices(currency);
    const config = await checkoutAPIService.getConfig();
    const subscription = await checkoutAPIService.checkSubscription();

    const intervalCount = subscription?.data?.intervalCount;

    if (intervalCount || intervalCount === 0) {
      const cabinet = await checkoutAPIService.getCabinetLink();
      setCabinetLink(cabinet?.data?.url);
    }

    setPrices(checkoutPrices?.data?.[0]?.prices || []);
    setPublicKey(config?.data?.publicKey);
    setActiveSubscription(subscription?.data);
    setLoader(false);
  };

  const isActiveSubscription = (plan) => {
    return plan?.recurring?.intervalCount === activeSubscription?.intervalCount;
  };

  useEffect(() => {
    loadCheckoutData();
    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => {
      setIsBuyOrManageSubscriptionLoading(false);
    };
  }, []);
  const pricesToDisplay = prices.slice().reverse();

  const currencyMap = {
    usd: '$',
    eur: '€',
  };

  const triggerNotification = () => {
    actions.removeAlert({ id: 2 });

    if (!isSubscriptionValid) {
      actions.addAlert({
        text: t('labels:defaultPlanErrorMessage', { endTime: new Date().toLocaleDateString() }),
        type: 'error',
        variant: 'filled',
        id: 1,
      });
    } else if (isTrialSubscription && !subscriptionInfo?.intervalCount) {
      actions.addAlert({
        text: t('labels:defaultTrialWarningMessage', { endTime: subscriptionInfo?.currentPeriodEnd }),
        type: 'warning',
        variant: 'filled',
        id: 2,
      });
    }
  };

  useEffect(() => {
    triggerNotification();

    return () => {
      actions.removeAlert({ id: 1 });
      actions.removeAlert({ id: 2 });
    };
  }, [isSubscriptionValid, isTrialSubscription]);
  return (
    <Box sx={S.PaymentWrapper}>
      <Card sx={S.TopCard}>
        <Text variant={isMobile ? 'h3' : 'h2'}>{t('payments:title')}</Text>
      </Card>
      <Box sx={S.notificationContainer}>
        <Notification
          customColor={
            isSubscriptionValid
              ? 'linear-gradient(90deg, var(--blue600) 0%, var(--blue300) 100%);'
              : 'linear-gradient(90deg, var(--darkRed) 0%, var(--error) 100%);'
          }
        />
      </Box>
      <Box sx={S.PaymentContent}>
        <Card sx={S.PaymentSideBar} className={openBenefits ? 'opened-benefits' : ''}>
          {isMobile && (
            <ButtonBase disableRipple onClick={() => setOpenBenefits((prev) => !prev)}>
              <Text variant="h3">{t('payments:ourBenefits')}</Text>
              <KeyboardArrowDownIcon />
            </ButtonBase>
          )}
          <img className="sidebar-image" src={PhonesImage} alt="phones image" width={322} height={143} />
          {[...Array(8)].map((_, index) => (
            <Box sx={S.PaymentSideBarTextItem} key={t(`payments:list:a${index + 1}`)}>
              <CheckIcon sx={{ color: COLORS.primary, maxWidth: '18px' }} />
              <Text className="sidebar-text" variant="input15">
                {t(`payments:list:a${index + 1}`)}
              </Text>
            </Box>
          ))}
        </Card>
        <Box sx={S.RightBlock}>
          <FormControl sx={S.FormControlSelect}>
            <Select
              value={currency}
              displayEmpty
              onChange={handleChangeCurrency}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{
                PaperProps: {
                  sx: S.PaperSelect,
                },
              }}
            >
              <MenuItem value="eur">
                <span className="select-circle">
                  <CheckIcon />
                </span>
                EUR
              </MenuItem>
              <MenuItem value="usd">
                <span className="select-circle">
                  <CheckIcon />
                </span>
                USD
              </MenuItem>
            </Select>
          </FormControl>
          {loader ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={S.SelectedPeriodWrapper}>
              {pricesToDisplay.map((plan) => {
                const isYearly = plan.recurring?.interval === 'year';
                const isSelected = selectedPeriod === plan.recurring.intervalCount;
                const bill = getBillingTranslation(plan.recurring?.interval, plan.recurring?.intervalCount);

                //Save % calc
                const percentOff = plan?.percentOff || 50;
                const monthlyPrice = isYearly && plan.regularMonthlyPrice;
                const oldPrice = prices?.length ? prices[0].regularMonthlyPrice : (monthlyPrice / percentOff) * 100;

                return (
                  <ButtonBase
                    disableRipple
                    sx={S.SelectedPeriod}
                    className={isSelected ? 'selected-period' : ''}
                    key={plan.id}
                    onClick={() => handleChangeSubPeriod(plan.recurring.intervalCount)}
                  >
                    {isYearly && <Box sx={S.PaymentbestChoiceLabel}>{t('payments:bestValue')}</Box>}
                    {isActiveSubscription(plan) && <Box sx={S.ActiveSubscription}>{t('payments:active')}</Box>}
                    <Box sx={S.PeriodWrapper}>
                      <Box sx={S.PeriodCircle} className={isSelected ? 'selected-period' : ''}></Box>
                      <Text sx={S.PeriodTimeText} className={isYearly ? 'yearly-period' : ''}>
                        {plan.recurring?.intervalCount} {plan.recurring?.intervalDisplay}
                      </Text>
                      <Text sx={S.PeriodBillText} className={isYearly ? 'yearly-period' : ''}>
                        {bill}
                      </Text>
                    </Box>
                    <Box sx={S.AmountWrapper}>
                      <Box>
                        {isYearly && !currencyLoader && (
                          <span className="save-amount-text">
                            {currencyMap[plan.currency]}
                            {oldPrice}
                          </span>
                        )}
                        <span className="current-price">
                          {currencyLoader ? (
                            <CircularProgress size={18} />
                          ) : (
                            <>
                              {currencyMap[plan.currency]}
                              {plan.regularMonthlyPrice} /
                            </>
                          )}
                        </span>
                        <span className="month-text">{t('payments:month')}</span>
                      </Box>
                      {isYearly && (
                        <Text sx={S.SaveAnuallyText}>{t('payments:savePercentAnnually', { percentOff })}</Text>
                      )}
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          )}
          {!loader && (
            <Box sx={S.PaymentbestChoiceButtons}>
              <Text variant="inputSmall" sx={{ color: COLORS.grey400 }} color={COLORS.black06}>
                {t('payments:footerNote')}
              </Text>
              <Button
                variant="contained"
                onClick={handleBuyPress}
                disabled={activeSubscription.interval ? !activeSubscription.interval : !selectedPeriod}
                className="confirm-button"
                endIcon={isBuyOrManageSubscriptionLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
              >
                {cabinetLink ? t('payments:mangeSubscription') : t('payments:buyBtn')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
